import React, { useState, useEffect, createContext, useContext } from 'react';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cross-tab logout synchronization (Jun 2026 security update).
const authChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('scensob-auth') : null;

/**
 * Auth state — Phase B (iter90, Feb 22, 2026).
 *
 * The httpOnly `access_token` cookie is the SOURCE OF TRUTH for auth
 * (set by /api/auth/login, cleared by /api/auth/logout). This context
 * only mirrors the user profile in `user` state; the token itself is
 * no longer stored in localStorage.
 *
 * On mount we hit /api/auth/me — if the browser holds a valid cookie
 * the call succeeds and we rehydrate `user` from the response, otherwise
 * `user` stays null and the caller redirects to /login.
 *
 * `user` is still cached in localStorage as a non-sensitive UX
 * optimisation (avoids the flash of unauthed content on refresh). The
 * cookie is the security boundary — localStorage is purely a rendering
 * hint.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user');
      return cached ? JSON.parse(cached) : null;
    } catch (_e) { return null; }
  });
  const [loading, setLoading] = useState(true);

  // When any other tab logs out (or a session expires), mirror it here.
  useEffect(() => {
    if (!authChannel) return undefined;
    const onMsg = (ev) => {
      if (ev?.data?.type === 'logout') {
        setUser(null);
        try { localStorage.removeItem('user'); } catch (_e) { /* ignore */ }
        const reason = ev.data.reason;
        window.location.assign(reason ? `/login?expired=${reason}` : '/login');
      }
    };
    authChannel.addEventListener('message', onMsg);
    return () => authChannel.removeEventListener('message', onMsg);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (!cancelled && resp.ok) {
          const data = await resp.json();
          const fresh = data?.user || null;
          setUser(fresh);
          if (fresh) {
            try { localStorage.setItem('user', JSON.stringify(fresh)); } catch (_e) { /* private mode / quota */ }
          }
        } else if (!cancelled && resp.status === 401) {
          // Cookie missing / expired — clear stale user cache so the UI
          // doesn't render as "logged in" against a dead session.
          setUser(null);
          try { localStorage.removeItem('user'); } catch (_e) { /* ignore */ }
        }
      } catch (_e) {
        // Network hiccup — keep whatever we had cached in state.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = (_token, userData) => {
    // `_token` arg preserved for signature back-compat, but we intentionally
    // ignore it — the cookie set by the login endpoint is all we need.
    try { localStorage.setItem('user', JSON.stringify(userData)); } catch (_e) { /* private mode / quota */ }
    setUser(userData);
  };

  const logout = (opts = {}) => {
    // Fire-and-forget cookie clear on the server. The redirect below runs
    // immediately — the request completes in the background.
    try {
      fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_e) { /* logout is best-effort; local purge below is authoritative */ }

    // Purge every place auth artefacts might live.
    try {
      // Legacy token (Phase A) may still be present in old browsers — clear it.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      Object.keys(localStorage).forEach((k) => {
        if (/^(auth|jwt|session|profile|push)/i.test(k)) {
          localStorage.removeItem(k);
        }
      });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('logout localStorage purge failed:', e?.message);
    }
    try { sessionStorage.clear(); } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('logout sessionStorage clear failed:', e?.message);
    }
    setUser(null);

    // Tell every other open tab to log out too.
    try { authChannel?.postMessage({ type: 'logout', reason: opts.reason || null }); } catch (_e) { /* ignore */ }

    if (!opts.silent && typeof window !== 'undefined') {
      window.location.assign(opts.reason ? `/login?expired=${opts.reason}` : '/logout');
    }
  };

  const updateUser = (userData) => {
    try { localStorage.setItem('user', JSON.stringify(userData)); } catch (_e) { /* ignore */ }
    setUser(userData);
  };

  // `token` kept as `null` in the exported value for backwards compatibility
  // with any code that still destructures it — nothing in the tree currently
  // does, but this makes future breakage graceful (undefined vs null).
  return (
    <AuthContext.Provider value={{ user, token: null, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
